import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CountryCode } from '~/api/common/types';

import { countryCodes, europeanCountryCodes } from '~/constants/countries';
import { useIntl } from '~/hooks/useIntl';
import { includes } from '~/utils/arrays';

export type CountryCodeGroup = { items: CountryCode[]; label: string };

/**
 * The country codes as a European group and an international group, each sorted by their localised country
 * name. Shaped for the `items` prop of a grouped combobox.
 */
export const useCountryCodeGroups = (): CountryCodeGroup[] => {
  const { displayNameOfCountry } = useIntl();
  const { t } = useTranslation(['clients']);

  return useMemo(() => {
    const sortByCountryName = (countryCodes: CountryCode[]) =>
      countryCodes.toSorted((a, b) => displayNameOfCountry(a).localeCompare(displayNameOfCountry(b)));

    return [
      {
        items: sortByCountryName(countryCodes.filter((countryCode) => includes(europeanCountryCodes, countryCode))),
        label: t('clients:fields.countryCode.groups.europe'),
      },
      {
        items: sortByCountryName(countryCodes.filter((countryCode) => !includes(europeanCountryCodes, countryCode))),
        label: t('clients:fields.countryCode.groups.international'),
      },
    ];
  }, [displayNameOfCountry, t]);
};
